import { Alert, AlertDescription, AlertTitle, Button, Icon } from "@/shared/components";
import { logError } from "@/shared/utils/datadog-logger";
import { formatErrorMessage } from "@/shared/utils/error-formatting";
import { useEffect } from "react";

interface ErrorDisplayProps {
    error: Error;
    title?: string;
    onRetry?: () => void;
    variant?: "section" | "inline";
}

/**
 * Reusable error display component for consistent error handling across the application.
 *
 * Variants:
 * - inline: Compact inline error for smaller UI areas (default)
 * - section: Section-level error with medium heading
 *
 * @example
 * ```tsx
 * // Section-level error
 * <ErrorDisplay
 *   error={error}
 *   variant="section"
 *   title="Fout bij het laden van gegevens"
 *   onRetry={() => window.location.reload()}
 * />
 *
 * // Inline error
 * <ErrorDisplay
 *   error={error}
 *   variant="inline"
 *   title="Fout bij het laden"
 * />
 * ```
 */
export function ErrorDisplay({ error, title, onRetry, variant = "inline" }: ErrorDisplayProps) {
    useEffect(() => {
        logError("ErrorDisplay rendered", error, { variant });
    }, [error, variant]);

    const errorMessage = formatErrorMessage(error);
    const defaultTitle = "Er is een fout opgetreden";

    // Inline variant - compact display
    if (variant === "inline") {
        return (
            <Alert variant="destructive" className="flex items-start gap-2 p-3" data-testid="error-display">
                <Icon name="error" className="text-destructive h-5 w-5 flex-shrink-0" />
                <div className="flex-1">
                    <AlertDescription className="text-s">
                        {title || defaultTitle} - {errorMessage}
                    </AlertDescription>
                </div>
                {onRetry && (
                    <Button size="s" variant="tertiary" onClick={onRetry} className="text-destructive hover:bg-destructive/20">
                        Opnieuw
                    </Button>
                )}
            </Alert>
        );
    }

    // Section variant (default) - section-level error
    return (
        <Alert variant="destructive" className="p-4" data-testid="error-display">
            <div>
                <div className="mb-2 flex items-center gap-2">
                    <Icon name="error" className="text-destructive flex-shrink-0" />
                    <AlertTitle className="text-m">{title || defaultTitle}</AlertTitle>
                </div>
                <div className="flex-1">
                    <AlertDescription className="text-s mb-4">{errorMessage}</AlertDescription>

                    {onRetry && (
                        <Button
                            size="s"
                            variant="tertiary"
                            onClick={onRetry}
                            className="bg-destructive/20 hover:bg-destructive/30 text-destructive"
                        >
                            Probeer opnieuw
                        </Button>
                    )}
                </div>
            </div>
        </Alert>
    );
}

type ErrorAlertProps = {
    description?: string;
    onRetry?: () => void;
};

export function ErrorAlert({ description, onRetry }: ErrorAlertProps) {
    return (
        <Alert variant="destructive" className="flex items-start gap-2 p-3" data-testid="error-alert">
            <Icon name="error" className="text-destructive h-5 w-5 flex-shrink-0" />
            <div className="flex-1">
                <AlertDescription className="text-s">{description}</AlertDescription>
            </div>
            {onRetry && (
                <Button size="s" variant="tertiary" onClick={onRetry} className="text-destructive hover:bg-destructive/20">
                    Opnieuw
                </Button>
            )}
        </Alert>
    );
}
